export type AssessmentSubject = "Mathematics" | "English";

interface AssessmentStatusPayload {
    SchoolCode: string;
    FamilyCode: string;
    Login: string;
    Status: "N";
    Subject: AssessmentSubject;
}

interface AssessmentStatusResponse {
    status?: unknown;
}

const inFlightAssessmentStatusRequests = new Map<string, Promise<string>>();
const cachedAssessmentStatuses = new Map<
    string,
    { status: string; expiresAt: number }
>();
const assessmentStatusCacheDurationMs = 5000;

export const clearCachedAssessmentStatuses = (): void => {
    cachedAssessmentStatuses.clear();
};

export const fetchAssessmentStatus = (
    apiUrl: string | undefined,
    schoolCode: string,
    familyCode: string,
    login: string,
    subject: AssessmentSubject,
    forceRefresh = false,
): Promise<string> => {
    const endpoint = subject === "Mathematics" ? "assigned_md/" : "assigned_ec/";
    const url = `${apiUrl}${endpoint}`;
    const payload: AssessmentStatusPayload = {
        SchoolCode: `ELW_${schoolCode}`,
        FamilyCode: familyCode,
        Login: login,
        Status: "N",
        Subject: subject,
    };
    const key = JSON.stringify([url, payload]);
    const existingRequest = inFlightAssessmentStatusRequests.get(key);

    if (existingRequest) return existingRequest;

    const cachedStatus = cachedAssessmentStatuses.get(key);
    if (cachedStatus && cachedStatus.expiresAt > Date.now() && !forceRefresh) {
        return Promise.resolve(cachedStatus.status);
    }
    cachedAssessmentStatuses.delete(key);

    const request = Promise.resolve()
        .then(() => fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        }))
        .then((response) => response.json())
        .then((data: AssessmentStatusResponse) => {
            const status = String(data?.status ?? "").trim();
            cachedAssessmentStatuses.set(key, {
                status,
                expiresAt: Date.now() + assessmentStatusCacheDurationMs,
            });
            return status;
        })
        .finally(() => {
            if (inFlightAssessmentStatusRequests.get(key) === request) {
                inFlightAssessmentStatusRequests.delete(key);
            }
        });

    inFlightAssessmentStatusRequests.set(key, request);
    return request;
};
